import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, Loader2, RefreshCw, BookOpen } from 'lucide-react';
import Tip from '../../components/Tip';

const API_URL = process.env.REACT_APP_API_URL || 'https://tradesavvy-production.up.railway.app';

interface WatchlistItem {
  pair: string;
  symbol: string;
  name: string;
}

interface IndicatorData {
  value: number | string | null;
  score: number;
  weight: number;
}

interface TimeframeResult {
  signal: 'BUY' | 'SELL' | 'HOLD';
  confidence: number;
  composite_score: number;
  trend: 'UPTREND' | 'DOWNTREND' | 'NEUTRAL';
  indicators: Record<string, IndicatorData>;
}

interface CoinResult {
  symbol: string;
  price: number;
  change24h: number;
  timeframes: Record<string, TimeframeResult>;
}

interface TechnicalTabProps {
  watchlist: WatchlistItem[];
  apiUrl?: string;
}

const TIMEFRAMES = ['15m', '1h', '4h', '1d'];

const INDICATOR_LABELS: Record<string, string> = {
  rsi: 'RSI',
  macd: 'MACD',
  sma: 'SMA',
  bb: 'BB',
  stoch_rsi: 'StochRSI',
  cci: 'CCI',
  williams_r: 'Wm%R',
  adx: 'ADX',
};

const INDICATOR_TIPS: Record<string, string> = {
  rsi: 'Relative Strength Index (14-period). Below 40 and rising = bullish (+15). Above 60 and falling = bearish (-15). Measures momentum speed.',
  macd: 'Moving Average Convergence/Divergence. Bullish crossover = +20, bearish = -20. The heaviest-weighted indicator — detects trend shifts.',
  sma: 'Simple Moving Average crossover. 20-day above 50-day = bullish (+15). Confirms the medium-term trend direction.',
  bb: 'Bollinger Bands (20, 2). Price near lower band = oversold (+10). Near upper band = overbought (-10). Measures price extremes.',
  stoch_rsi: 'Stochastic RSI. Below 20 = oversold/bullish (+10). Above 80 = overbought/bearish (-10). More sensitive than standard RSI.',
  cci: 'Commodity Channel Index (20). Below -100 = oversold (+10). Above +100 = overbought (-10). Identifies cyclical turning points.',
  williams_r: 'Williams %R (14). Below -80 = oversold/bullish (+10). Above -20 = overbought/bearish (-10). Fast-moving momentum oscillator.',
  adx: 'Average Directional Index (14). Above 25 = trending market — amplifies the existing signal direction (+10 or -10). Below 25 = no additional score.',
};

const TechnicalTab: React.FC<TechnicalTabProps> = ({ watchlist, apiUrl }) => {
  const [data, setData] = useState<CoinResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const url = apiUrl || API_URL;

  const fetchData = useCallback(async () => {
    if (watchlist.length === 0) return;
    setLoading(true);
    try {
      const symbols = watchlist.map(w => w.pair).join(',');
      const response = await fetch(`${url}/api/signals/technical?symbols=${symbols}`, {
        signal: AbortSignal.timeout(60000),
      });
      const result = await response.json();
      setData(Array.isArray(result) ? result : []);
      setLastRefresh(new Date());
    } catch (e) {
      console.error('Technical fetch error:', e);
    }
    setLoading(false);
  }, [watchlist, url]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const nameMap: Record<string, string> = {};
  watchlist.forEach(w => { nameMap[w.symbol] = w.name; });

  const getScoreColor = (score: number) => {
    if (score >= 40) return '#22c55e';
    if (score <= -40) return '#ef4444';
    if (score > 0) return '#86efac';
    if (score < 0) return '#fca5a5';
    return '#6b7280';
  };

  const getScoreBarWidth = (score: number) => {
    return Math.abs(score);
  };

  const getSignalColor = (signal: string) => {
    if (signal === 'BUY') return 'text-green-500';
    if (signal === 'SELL') return 'text-red-500';
    return 'text-gray-400';
  };

  const getSignalIcon = (signal: string) => {
    if (signal === 'BUY') return <TrendingUp className="w-5 h-5" />;
    if (signal === 'SELL') return <TrendingDown className="w-5 h-5" />;
    return <Minus className="w-5 h-5" />;
  };

  const getTrendColor = (trend: string) => {
    if (trend === 'UPTREND') return 'bg-green-500/10 text-green-500';
    if (trend === 'DOWNTREND') return 'bg-red-500/10 text-red-500';
    return 'bg-gray-500/10 text-gray-400';
  };

  const formatPrice = (price: number): string => {
    if (price === 0) return '0.00';
    if (price >= 1) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const decimals = Math.max(2, -Math.floor(Math.log10(price)) + 2);
    return price.toFixed(Math.min(decimals, 8));
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div />
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 border border-gray-700 hover:border-gray-600 rounded text-xs text-gray-300 hover:text-white transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className="flex items-center gap-3 mb-3 px-3 py-2 bg-blue-500/5 border border-blue-500/10 rounded-lg">
        <span className="text-xs text-gray-400">8-indicator weighted composite (-100 to +100) &middot; BUY &ge; 40, SELL &le; -40</span>
        <a href="/guides/guide-technical.html" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded text-xs font-medium transition-colors whitespace-nowrap">
          <BookOpen className="w-3.5 h-3.5" />
          How to read this
        </a>
      </div>

      {lastRefresh && (
        <div style={{ marginBottom: 12, fontSize: 11, color: '#52525b' }}>
          Updated: {lastRefresh.toLocaleTimeString()} &middot; Auto-refreshes every 60s
        </div>
      )}

      {loading && data.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-7 h-7 text-blue-500 animate-spin mr-3" />
          <span className="text-gray-400 text-sm">Computing 8 indicators across 4 timeframes...</span>
        </div>
      )}

      <div className="grid gap-5">
        {data.map(coin => (
          <div key={coin.symbol} className="bg-gray-900 border border-gray-800 rounded-lg p-5 hover:border-gray-700 transition-colors">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-xl font-bold">{coin.symbol}-USD</h2>
                <p className="text-gray-400 text-xs">{nameMap[coin.symbol] || coin.symbol}</p>
              </div>
              <div className="text-right">
                <div className="text-xl font-mono">${formatPrice(coin.price)}</div>
                <div className={`text-xs ${coin.change24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {coin.change24h >= 0 ? '+' : ''}{coin.change24h.toFixed(2)}%
                </div>
              </div>
            </div>

            {/* Timeframe grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {TIMEFRAMES.map(tf => {
                const tfData = coin.timeframes?.[tf];
                if (!tfData) return null;
                return (
                  <div key={tf} className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                    <div className="text-xs text-gray-400 uppercase mb-2">{tf}</div>

                    {/* Signal + Score */}
                    <div className={`flex items-center gap-2 mb-2 ${getSignalColor(tfData.signal)}`}>
                      {getSignalIcon(tfData.signal)}
                      <span className="font-bold text-sm">{tfData.signal}</span>
                      <Tip text="Composite score from all 8 indicators. BUY when >= +40, SELL when <= -40, HOLD in between. Higher magnitude = stronger conviction." />
                      <span className="ml-auto text-xs font-mono" style={{ color: getScoreColor(tfData.composite_score) }}>
                        {tfData.composite_score > 0 ? '+' : ''}{tfData.composite_score}
                      </span>
                    </div>

                    {/* Score bar */}
                    <div className="relative mb-2 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div className="absolute inset-y-0 left-1/2 w-px bg-gray-600 z-10" />
                      {tfData.composite_score > 0 ? (
                        <div
                          className="absolute inset-y-0 left-1/2 rounded-r-full"
                          style={{
                            width: `${getScoreBarWidth(tfData.composite_score) / 2}%`,
                            backgroundColor: getScoreColor(tfData.composite_score),
                          }}
                        />
                      ) : tfData.composite_score < 0 ? (
                        <div
                          className="absolute inset-y-0 rounded-l-full"
                          style={{
                            right: '50%',
                            width: `${getScoreBarWidth(tfData.composite_score) / 2}%`,
                            backgroundColor: getScoreColor(tfData.composite_score),
                          }}
                        />
                      ) : null}
                    </div>

                    {/* Confidence */}
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-400">Confidence <Tip text="How strongly the indicators agree. Equals the absolute value of the composite score. 70%+ = high conviction, 40-69% = moderate, below 40% = weak." /></span>
                      <span className="font-mono">{tfData.confidence}%</span>
                    </div>

                    {/* Trend badge */}
                    <div className={`text-xs px-2 py-0.5 rounded text-center mb-2 ${getTrendColor(tfData.trend)}`}>
                      {tfData.trend} <Tip text="UPTREND: price above SMA(200) and 20-day SMA above 50-day. DOWNTREND: opposite. NEUTRAL: mixed signals." />
                    </div>

                    {/* Individual indicators */}
                    <div className="space-y-0.5">
                      {Object.entries(tfData.indicators).map(([key, ind]) => (
                        <div key={key} className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">{INDICATOR_LABELS[key] || key}{INDICATOR_TIPS[key] && <Tip text={INDICATOR_TIPS[key]} />}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-gray-400 font-mono text-[10px]">
                              {ind.value !== null ? String(ind.value) : '-'}
                            </span>
                            <span
                              className="font-mono w-6 text-right"
                              style={{ color: ind.score > 0 ? '#22c55e' : ind.score < 0 ? '#ef4444' : '#6b7280', fontSize: 10 }}
                            >
                              {ind.score > 0 ? '+' : ''}{ind.score}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TechnicalTab;
